import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getBookingDetailById } from "../../Services/HotelSearchApi";

const HotelPrintInvoice = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const referenceNo =
    queryParams.get("ReferenceNo") || queryParams.get("referenceno");

  const [bookingDetails, setBookingDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (referenceNo) {
      fetchHotelBookingDetails(referenceNo);
    } else {
      console.warn("Missing referenceNo ");
    }
  }, [referenceNo]);

  const fetchHotelBookingDetails = async (refNo) => {
    try {
      setLoading(true);
      setError("");
      let response = await getBookingDetailById(refNo);
      if (Array.isArray(response) && response.length > 0) response = response[0];

      // Defensively handle both wrapped and direct responses
      if (response && response.booking) {
        setBookingDetails(response);
      } else if (response && response.Booking) {
        setBookingDetails({ ...response, booking: response.Booking });
      } else if (response && response.pkid) {
        setBookingDetails({ booking: response });
      } else {
        setBookingDetails(null);
        setError("Booking details not found.");
      }
    } catch (err) {
      setError("Failed to load hotel booking details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  useEffect(() => {
    if (bookingDetails) {
      const timer = setTimeout(() => {
        window.print();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [bookingDetails]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 bg-white">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  if (!bookingDetails || !bookingDetails.booking) {
    return null;
  }

  const { booking } = bookingDetails;
  const room = booking.rooms?.[0] || {};
  const paxDetails = room.paxDetails || [];

  // Fallback map: use new response fields or fall back to older structure
  const companyData = booking.companynGstDetails || bookingDetails.companyDetails || {};
  const corporateData = {
    companyName: booking.gstName || bookingDetails.corporateDetails?.companyName,
    address: booking.address || bookingDetails.corporateDetails?.address,
    email: booking.email || bookingDetails.corporateDetails?.email,
    phoneNumber: booking.phone || bookingDetails.corporateDetails?.phoneNumber,
    gstNumber: booking.gstNumber || bookingDetails.corporateDetails?.gstNumber,
    city: bookingDetails.corporateDetails?.city || "",
    country: bookingDetails.corporateDetails?.country || "",
    pinCode: bookingDetails.corporateDetails?.pinCode || "",
    state: bookingDetails.corporateDetails?.state || ""
  };

  return (
    <div id="view-content" style={{ background: "white", minHeight: "100vh" }}>
      <div className="header-bg d-flex justify-content-between align-items-center p-4 border-bottom">
        <div className="invoice_logo">
          {/* Use local asset or placeholder */}
          <img
            src="/assets/images/logo.png"
            alt="Logo"
            style={{ maxHeight: "60px" }}
          />
        </div>
        <div className="top_details text-end">
          <p className="mb-1">
            <strong>Invoice Date:</strong>{" "}
            <span style={{ color: "red", fontWeight: "600" }}>
              {formatDate(booking.createdAt || booking.bookingDate)}
            </span>
          </p>
        </div>
      </div>

      <div className="invoice_body p-4">
        <div className="agent_container mb-4">
          <h2
            className="text-center mb-4 p-2 bg-light border rounded shadow-sm"
            style={{ fontSize: "1.2rem", fontWeight: "700" }}
          >
            <span>
              Invoice Number -{" "}
              {booking.invoiceNumber || booking.bookingRefId || "N/A"}
            </span>
          </h2>
          <div className="row g-4">
            <div className="col-md-6">
              <h3
                className="border-bottom pb-2 mb-3"
                style={{ fontSize: "1rem", color: "#110942" }}
              >
                From
              </h3>
              <h4
                className="text-capitalize mb-2"
                style={{ fontSize: "1.1rem" }}
              >
                {companyData?.name || "BERRY JOURNEYS PRIVATE LIMITED"}
              </h4>
              <p className="mb-1 small">
                <strong>Regd Office:</strong> {companyData?.address || "N/A"}
              </p>
              {(companyData?.city || companyData?.country || companyData?.pincode) && (
                <p className="mb-1 small">
                  {[companyData?.city, companyData?.country].filter(Boolean).join(", ")}
                  {companyData?.pincode ? ` - ${companyData.pincode}` : ""}
                </p>
              )}
              <p className="mb-1 small">
                <strong>Email:</strong> {companyData?.email}
              </p>
              <p className="mb-1 small">
                <strong>Phone:</strong> {companyData?.phone}
              </p>
              <p className="mb-1 small">
                <strong>State:</strong> {companyData?.gstState}
              </p>
              <p className="mb-1 small">
                <strong>GSTIN:</strong> {companyData?.gstNo}
              </p>
              <p className="mb-1 small">
                <strong>CIN:</strong> U93000UR2010PTC033270
              </p>
            </div>
            <div className="col-md-6">
              <h3
                className="border-bottom pb-2 mb-3"
                style={{ fontSize: "1rem", color: "#110942" }}
              >
                To
              </h3>
              <h4
                className="text-capitalize mb-2"
                style={{ fontSize: "1.1rem" }}
              >
                {corporateData?.companyName || "N/A"}
              </h4>
              <p className="mb-1 small">
                <strong>Address:</strong> {corporateData?.address || "N/A"}
              </p>
              {(corporateData?.city || corporateData?.country || corporateData?.pinCode) && (
                <p className="mb-1 small">
                  {[corporateData?.city, corporateData?.country].filter(Boolean).join(", ")}
                  {corporateData?.pinCode ? ` - ${corporateData.pinCode}` : ""}
                </p>
              )}
              <p className="mb-1 small">
                <strong>Email:</strong> {corporateData?.email}
              </p>
              <p className="mb-1 small">
                <strong>Phone:</strong> {corporateData?.phoneNumber}
              </p>
              {corporateData?.state && (
                <p className="mb-1 small">
                  <strong>State:</strong> {corporateData?.state}
                </p>
              )}
              <p className="mb-1 small">
                <strong>GST Number:</strong> {corporateData?.gstNumber}
              </p>
            </div>
          </div>
        </div>

        <div className="ticket_details-container mb-4">
          <div className="table-responsive">
            <table className="table table-bordered small align-middle">
              <thead className="table-light">
                <tr className="text-center">
                  <th>S No.</th>
                  <th>Booking Ref</th>
                  <th>Hotel Name</th>
                  <th>City</th>
                  <th>Check-In</th>
                  <th>Check-Out</th>
                  <th>Room Type</th>
                  <th>Room Price</th>
                  <th>Taxes</th>
                  {/* <th>GST</th> */}
                  <th>Mgmt Fee</th>
                  <th>Mgmt Tax</th>
                  <th>Total Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="text-center">
                  <td>1</td>
                  <td>{booking.bookingRefId || booking.confirmationNo}</td>
                  <td>{booking.hotelName}</td>
                  <td>{booking.cityName}</td>
                  <td>{formatDate(booking.checkInDate)}</td>
                  <td>{formatDate(booking.checkOutDate)}</td>
                  <td>{room.roomName || room.roomId || "N/A"}</td>
                  <td>
                    {booking.currency}{" "}
                    {Math.round(booking.basePrice || 0).toLocaleString()}
                  </td>
                  <td>{Math.round(booking.totalTax || 0).toLocaleString()}</td>
                  {/* <td>{Math.round(booking.gst || 0).toLocaleString()}</td> */}
                  <td>{Math.round(booking.managementFee || 0).toLocaleString()}</td>
                  <td>{Math.round(booking.managementFeeTax || 0).toLocaleString()}</td>
                  <td className="fw-bold">
                    {booking.currency}{" "}
                    {Math.round(booking.totalPrice || 0).toLocaleString()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="traveler_info mb-4">
          <h3
            className="address-head mb-3"
            style={{
              fontSize: "1rem",
              color: "#110942",
              borderLeft: "4px solid #110942",
              paddingLeft: "10px",
            }}
          >
            Guest Details
          </h3>
          <div className="table-responsive">
            <table className="table table-bordered small">
              <thead className="table-light">
                <tr>
                  <th>S No.</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Is Lead</th>
                </tr>
              </thead>
              <tbody>
                {paxDetails.map((pax, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>
                      {pax.title} {pax.firstName} {pax.lastName}
                    </td>
                    <td>{pax.paxType || "Adult"}</td>
                    <td>{pax.isPrimary || pax.isLead ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bill_container mb-4">
          <div className="row">
            <div className="col-md-6">
              <h3 className="address-head mb-2" style={{ fontSize: "1rem" }}>
                GST Breakup
              </h3>
              <table className="table table-bordered small">
                <thead className="table-light">
                  <tr>
                    <th>Description</th>
                    <th>CGST</th>
                    <th>SGST</th>
                    <th>IGST</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Management Fee GST</td>
                    <td>{Math.round(booking.cgst || 0).toLocaleString()}</td>
                    <td>{Math.round(booking.sgst || 0).toLocaleString()}</td>
                    <td>{Math.round(booking.igst || 0).toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="col-md-6">
              <table className="table table-bordered shadow-sm">
                <tbody>
                  <tr>
                    <td>Hotel Room Price:</td>
                    <td className="text-end">
                      {booking.currency} {Math.round(booking.basePrice || 0).toLocaleString()}
                    </td>
                  </tr>
                  {Number(booking.totalTax || 0) > 0 && (
                    <tr>
                      <td>Hotel Taxes (incl. GST):</td>
                      <td className="text-end">
                        {booking.currency} {Math.round(booking.totalTax || 0).toLocaleString()}
                      </td>
                    </tr>
                  )}
                  {/* {Number(booking.gst || 0) > 0 && (
                    <tr>
                      <td>GST (Inclusive):</td>
                      <td className="text-end">
                        {booking.currency} {Math.round(booking.gst || 0).toLocaleString()}
                      </td>
                    </tr>
                  )} */}
                  {Number(booking.managementFee || 0) > 0 && (
                    <tr>
                      <td>Management Fee:</td>
                      <td className="text-end">
                        {booking.currency} {Math.round(booking.managementFee || 0).toLocaleString()}
                      </td>
                    </tr>
                  )}
                  {Number(booking.managementFeeTax || 0) > 0 && (
                    <tr>
                      <td>Management Fee GST:</td>
                      <td className="text-end">
                        {booking.currency} {Math.round(booking.managementFeeTax || 0).toLocaleString()}
                      </td>
                    </tr>
                  )}
                  <tr className="table-active">
                    <td className="fw-bold fs-5">Total Amount:</td>
                    <td className="text-end fw-bold fs-5 text-primary">
                      {booking.currency} {Math.round(booking.totalPrice || 0).toLocaleString()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {booking.gstRequest && booking.gstRequest.gstNumber && (
          <div className="passenger_gst mb-4">
            <h3 className="address-head mb-2" style={{ fontSize: "1rem" }}>
              Passenger GST Details
            </h3>
            <table className="table table-bordered small text-center">
              <thead style={{ background: "#110942", color: "white" }}>
                <tr>
                  <th>GST Name</th>
                  <th>GST No</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Address</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{booking.gstRequest.name || "N/A"}</td>
                  <td>{booking.gstRequest.gstNumber || "N/A"}</td>
                  <td>{booking.gstRequest.email || "N/A"}</td>
                  <td>{booking.gstRequest.contactNo || "N/A"}</td>
                  <td>{booking.gstRequest.address || "N/A"}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="footer-terms border-top pt-3">
          <h3 className="address-head mb-2" style={{ fontSize: "0.9rem" }}>
            Terms and Conditions E. & O.E:
          </h3>
          <ol className="small text-muted" style={{ paddingLeft: "1.2rem" }}>
            <li>
              Payment to be made as per contracted terms and printed official
              receipt must be obtained.
            </li>
            <li>
              All cheques / demand drafts in payment of bills must be crossed
              'A/c Payee Only' and drawn in favour of BERRY JOURNEYS PRIVATE
              LIMITED
            </li>
            <li>
              Interest @ 24% per annum will be charged on all outstanding bills
              after due date.
            </li>
            <li>
              Kindly check all details carefully before accepting the
              vouchers/utilizing the service.
            </li>
            <li>
              Refunds & Cancellations are subject to Hotel/Supplier approval.
            </li>
            <li>Please resolve any discrepancies with this invoice within 30 days of the invoice date.</li>
            <li>Subject To HARYANA Jurisdiction</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default HotelPrintInvoice;
